import { useMemo, useCallback, useRef, useEffect } from "react";
import { GoogleMap, useJsApiLoader, Marker, Polyline } from "@react-google-maps/api";

const defaultCenter = { lat: 39.8283, lng: -98.5795 };

const mapContainerStyle = { width: "100%", height: 320 };

const SEGMENT_STROKE_COLORS = ["#1976d2", "#d32f2f", "#388e3c", "#f57c00", "#7b1fa2", "#00838f", "#c2185b"];

function sortCrewKeys(keys) {
  const uniq = [...new Set(keys)];
  const un = uniq.filter((k) => k === "unassigned");
  const rest = uniq.filter((k) => k !== "unassigned").sort((a, b) => a.localeCompare(b));
  return [...un, ...rest];
}

function jobCrewKey(j) {
  return j.assignedCrewMemberId ? String(j.assignedCrewMemberId) : "unassigned";
}

/**
 * Numbered job markers + optional driving polylines per crew segment (operator dashboard).
 * @param {{
 *   jobs: { id: string, propertyAddress: string, routeOrder: number|null, assignedCrewMemberId?: string|null, location?: { lat: number, lng: number }|null }[],
 *   apiKey: string,
 *   routeSegments?: { crewKey: string, encodedPolyline?: string }[],
 * }} props
 */
export default function AdminSchedulingMap({ jobs = [], apiKey, routeSegments = [] }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "admin-schedule-map",
    googleMapsApiKey: apiKey || "",
    libraries: ["geometry"],
  });

  const mapRef = useRef(null);

  const segmentKeyOrder = useMemo(() => sortCrewKeys((jobs || []).map(jobCrewKey)), [jobs]);

  const colorByCrewKey = useMemo(() => {
    const m = new Map();
    segmentKeyOrder.forEach((k, i) => m.set(k, SEGMENT_STROKE_COLORS[i % SEGMENT_STROKE_COLORS.length]));
    return m;
  }, [segmentKeyOrder]);

  const orderInCrew = useMemo(() => {
    const m = new Map();
    const sorted = [...(jobs || [])].sort((a, b) => {
      const ka = jobCrewKey(a);
      const kb = jobCrewKey(b);
      if (ka !== kb) {
        if (ka === "unassigned") return -1;
        if (kb === "unassigned") return 1;
        return ka.localeCompare(kb);
      }
      const oa = a.routeOrder != null ? Number(a.routeOrder) : 999999;
      const ob = b.routeOrder != null ? Number(b.routeOrder) : 999999;
      return oa - ob;
    });
    const counters = new Map();
    sorted.forEach((j) => {
      const ck = jobCrewKey(j);
      const n = (counters.get(ck) || 0) + 1;
      counters.set(ck, n);
      m.set(j.id, n);
    });
    return m;
  }, [jobs]);

  const withCoords = useMemo(
    () =>
      (jobs || [])
        .filter((j) => j.location && typeof j.location.lat === "number" && typeof j.location.lng === "number")
        .sort((a, b) => {
          const ka = jobCrewKey(a);
          const kb = jobCrewKey(b);
          if (ka !== kb) {
            if (ka === "unassigned") return -1;
            if (kb === "unassigned") return 1;
            return ka.localeCompare(kb);
          }
          const oa = a.routeOrder != null ? Number(a.routeOrder) : 999999;
          const ob = b.routeOrder != null ? Number(b.routeOrder) : 999999;
          return oa - ob;
        }),
    [jobs]
  );

  const decodedPolylines = useMemo(() => {
    if (!isLoaded || !window.google?.maps?.geometry?.encoding) return [];
    const list = [];
    const segs = Array.isArray(routeSegments) ? routeSegments : [];
    for (const seg of segs) {
      const enc = seg.encodedPolyline && String(seg.encodedPolyline).trim();
      if (!enc) continue;
      try {
        const path = window.google.maps.geometry.encoding.decodePath(enc);
        if (path && path.length) {
          list.push({ crewKey: seg.crewKey || "unassigned", path });
        }
      } catch {
        /* ignore bad polyline */
      }
    }
    return list;
  }, [routeSegments, isLoaded]);

  const center = useMemo(() => {
    if (!withCoords.length) return defaultCenter;
    const lat = withCoords.reduce((s, j) => s + j.location.lat, 0) / withCoords.length;
    const lng = withCoords.reduce((s, j) => s + j.location.lng, 0) / withCoords.length;
    return { lat, lng };
  }, [withCoords]);

  const fitBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps || !withCoords.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    withCoords.forEach((j) => bounds.extend({ lat: j.location.lat, lng: j.location.lng }));
    decodedPolylines.forEach(({ path }) => {
      path.forEach((ll) => bounds.extend(ll));
    });
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 48);
    }
  }, [withCoords, decodedPolylines]);

  useEffect(() => {
    fitBounds();
  }, [fitBounds]);

  const onMarkerClick = useCallback((address) => {
    if (!address) return;
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, []);

  const onMapLoad = useCallback((m) => {
    mapRef.current = m;
  }, []);

  if (!apiKey) {
    return null;
  }
  if (loadError) {
    return null;
  }
  if (!isLoaded) {
    return null;
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={withCoords.length ? 11 : 4}
      onLoad={onMapLoad}
    >
      {decodedPolylines.map(({ crewKey, path }, idx) => (
        <Polyline
          key={`${crewKey}-${idx}`}
          path={path}
          options={{
            strokeColor: colorByCrewKey.get(crewKey) || SEGMENT_STROKE_COLORS[0],
            strokeOpacity: 0.92,
            strokeWeight: 4,
          }}
        />
      ))}
      {withCoords.map((j, idx) => {
        const ck = jobCrewKey(j);
        const si = Math.max(0, segmentKeyOrder.indexOf(ck));
        const letter = String.fromCharCode(65 + si);
        const num = orderInCrew.get(j.id) ?? idx + 1;
        return (
          <Marker
            key={j.id}
            position={{ lat: j.location.lat, lng: j.location.lng }}
            label={{
              text: `${letter}${num}`,
              color: "white",
              fontSize: "11px",
            }}
            zIndex={idx}
            title={j.propertyAddress || ""}
            onClick={() => onMarkerClick(j.propertyAddress)}
          />
        );
      })}
    </GoogleMap>
  );
}
